/**
 * Site navigation — single source of truth for sidebar + routing.
 */

export type NavItem = {
  title: string;
  slug: string;
  stub?: boolean;
};

export type NavSection = {
  title: string;
  items: NavItem[];
};

export const SITE = {
  name: 'Valvur Wiki',
  tagline: 'What we do · What we have · Where we are going',
  homeSlug: 'get-started/overview',
  contactEmail: 'demo@valvur.gg',
  contactLabel: 'Need more info? Contact us',
  externalLinks: [
    { label: 'valvur.gg', href: 'https://valvur.gg' },
    { label: 'valvur.ai', href: 'https://valvur.ai' },
  ],
} as const;

export const NAV: NavSection[] = [
  {
    title: 'Get started',
    items: [
      { title: 'Overview', slug: 'get-started/overview' },
      { title: 'Who Valvur is for', slug: 'get-started/who-is-it-for' },
    ],
  },
  {
    title: 'Products',
    items: [
      { title: 'Kid app', slug: 'products/kid-app' },
      { title: 'Family hub', slug: 'products/family-hub' },
      { title: 'Patrol', slug: 'products/patrol' },
      { title: 'School', slug: 'products/school' },
      { title: 'Pulse', slug: 'products/pulse', stub: true },
    ],
  },
  {
    title: 'Identity & partners',
    items: [
      { title: 'Identity overview', slug: 'identity/overview' },
      { title: 'Verification (KYC)', slug: 'identity/kyc' },
      { title: 'Login with Valvur', slug: 'identity/login-with-valvur' },
      { title: 'Partner quick start', slug: 'identity/partner-quick-start' },
    ],
  },
  {
    title: 'Core concepts',
    items: [
      { title: 'Duties', slug: 'concepts/duties' },
      { title: 'Experience', slug: 'concepts/experience' },
      { title: 'Shield', slug: 'concepts/shield' },
      { title: 'Gold', slug: 'concepts/gold' },
      { title: 'Reputation', slug: 'concepts/reputation' },
      { title: 'Courses & learning', slug: 'concepts/courses' },
      { title: 'Dossier', slug: 'concepts/dossier' },
      { title: 'PQC', slug: 'concepts/pqc' },
    ],
  },
];

export function findNavItem(slug: string): NavItem | undefined {
  for (const section of NAV) {
    const found = section.items.find((item) => item.slug === slug);
    if (found) return found;
  }
  return undefined;
}

export function flattenNav(): NavItem[] {
  return NAV.flatMap((section) => section.items);
}

export function adjacentPages(slug: string): {
  prev?: NavItem;
  next?: NavItem;
} {
  const pages = flattenNav().filter((item) => !item.stub);
  const index = pages.findIndex((item) => item.slug === slug);
  if (index === -1) return {};
  return {
    prev: index > 0 ? pages[index - 1] : undefined,
    next: index < pages.length - 1 ? pages[index + 1] : undefined,
  };
}
